/**
 * 인증 API 호출 — backend /auth/* 엔드포인트와 1:1.
 *
 * 응답 분기 (Phase 5-1 사용 범위):
 *   login() → "session" 발급 또는 "mfa" 챌린지
 *   totpVerify() → 세션 발급 (+ device 등급은 신뢰 디바이스 등록)
 *   me() → 현재 로그인 사용자
 *   logout() → 세션 종료
 */

import { api } from './api';
import type { AuthUser, TotpEnrollStart, TrustedDevice } from './types';

export type LoginResult =
  | { kind: 'session'; user: AuthUser }
  | { kind: 'mfa'; challengeId: string; offerTrustDevice: boolean };

type RawLoginResponse =
  | { user: AuthUser }
  | { mfaRequired: true; challengeId: string; offerTrustDevice: boolean };

export async function login(userId: string, password: string): Promise<LoginResult> {
  const data = await api.post<RawLoginResponse>('/auth/login', { userId, password });
  if ('mfaRequired' in data) {
    return { kind: 'mfa', challengeId: data.challengeId, offerTrustDevice: data.offerTrustDevice };
  }
  return { kind: 'session', user: data.user };
}

export async function totpVerify(input: {
  challengeId: string;
  code: string;
  deviceName?: string;
}): Promise<{ user: AuthUser; trustedDeviceRegistered: boolean }> {
  return api.post('/auth/totp/verify', input);
}

/** 복구 코드로 2차 인증 (휴대폰 분실 등 · v2.231.0 · F-11) — 서버 통로는 처음부터 있었고 화면만 없었다 */
export async function totpRecovery(input: {
  challengeId: string;
  code: string;
}): Promise<{ user: AuthUser; recoveryCodesRemaining: number }> {
  return api.post('/auth/totp/recovery', input);
}

export async function me(): Promise<{ user: AuthUser }> {
  return api.get('/auth/me');
}

export async function logout(): Promise<{ loggedOut: boolean }> {
  return api.post('/auth/logout', {});
}

export async function requestPasswordReset(input: {
  userId: string;
  email: string;
}): Promise<{ sent: boolean; message: string }> {
  return api.post('/auth/password/reset/request', input);
}

export async function confirmPasswordReset(input: {
  token: string;
  newPassword: string;
}): Promise<{ reset: boolean }> {
  return api.post('/auth/password/reset/confirm', input);
}

// ─── 마이페이지 (Phase 5-2) ────────────────────────

/** 로그인 상태에서 본인 비밀번호 변경 */
export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ changed: boolean }> {
  return api.post('/auth/password/change', input);
}

/** TOTP 등록 시작 — QR + 시크릿 + 복구코드 (모두 클라이언트 1회 표시) */
export async function totpEnrollStart(): Promise<TotpEnrollStart> {
  return api.post('/auth/totp/enroll/start', {});
}

/** TOTP 등록 검증 — securityLevel: device(1단계) | mfa(2단계) */
export async function totpEnrollVerify(input: {
  code: string;
  securityLevel: 'device' | 'mfa';
  /** 등급이 device 일 때 지금 쓰는 기기에 붙일 이름. 비우면 서버가 브라우저 정보로 자동 (v2.57.2) */
  deviceName?: string;
}): Promise<{
  ok: boolean;
  securityLevel: 'device' | 'mfa';
  /** 이 기기가 신뢰 기기로 등록됐는가 (device 등급일 때 true) */
  trustedDeviceRegistered: boolean;
}> {
  return api.post('/auth/totp/enroll/verify', input);
}

/** 보안 등급 비활성화 (basic 으로 다운그레이드 + 모든 디바이스 해제) — 현재 비밀번호 필요 (v2.232.0 · F-S4) */
export async function disableSecurity(currentPassword: string): Promise<{
  securityLevel: 'basic';
  devicesRemoved: number;
}> {
  return api.post('/auth/security-level/disable', { currentPassword });
}

/** 본인 신뢰 디바이스 목록 */
export async function listDevices(): Promise<{ devices: TrustedDevice[] }> {
  return api.get('/auth/devices');
}

/** 본인 디바이스 1건 해제 */
export async function deleteDevice(deviceId: string): Promise<{ deleted: boolean }> {
  return api.delete(`/auth/devices/${encodeURIComponent(deviceId)}`);
}

// ─── 서명 관리 (마이페이지, v1.5.0) ─────────────────────
//
// 계정당 서명/직인 이미지 1장 — 판매보고서 결재판에 사용.

/** 본인 서명 이미지 URL (캐시 무효화용 ts 파라미터 지원) */
export function mySignatureUrl(cacheBust?: number): string {
  return `/api/auth/me/signature${cacheBust ? `?t=${cacheBust}` : ''}`;
}

/** 본인 서명 업로드 (PNG/JPG ≤ 2MB, 기존 서명 교체) */
export async function uploadMySignature(file: File): Promise<void> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/auth/me/signature', {
    method: 'PUT',
    credentials: 'include',
    body: fd,
  });
  const json = (await res.json()) as { ok: boolean; error?: { message: string } };
  if (!json.ok) throw new Error(json.error?.message ?? '서명 업로드 실패');
}

/** 본인 서명 삭제 */
export async function deleteMySignature(): Promise<void> {
  const res = await fetch('/api/auth/me/signature', {
    method: 'DELETE',
    credentials: 'include',
  });
  const json = (await res.json()) as { ok: boolean; error?: { message: string } };
  if (!json.ok) throw new Error(json.error?.message ?? '서명 삭제 실패');
}

/** 본인 서명 존재 여부 (HEAD 대신 GET 사용 — 404 면 없음) */
export async function hasMySignature(): Promise<boolean> {
  const res = await fetch('/api/auth/me/signature', { credentials: 'include' });
  return res.ok;
}
